import React, { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReflectionView = ({ conversationId, onDone }) => {
  const [reflection, setReflection] = useState(null);
  const [showTitle, setShowTitle] = useState(false);
  const [showDivider, setShowDivider] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchReflection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReflection = async () => {
    try {
      const res = await fetch(`${API}/conversations/${conversationId}/reflection`, { method: 'POST' });
      const data = await res.json();
      setReflection(data.reflection);

      // Sequence: title fade in → divider → options
      setTimeout(() => setShowTitle(true), 300);
      setTimeout(() => setShowDivider(true), 2000);
      setTimeout(() => setShowOptions(true), 2500);
    } catch {
      setReflection('a quiet moment between words');
      setTimeout(() => setShowTitle(true), 300);
      setTimeout(() => setShowDivider(true), 2000);
      setTimeout(() => setShowOptions(true), 2500);
    }
  };

  const generateShareCard = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !reflection) return null;

    const ctx = canvas.getContext('2d');
    const size = 1080;
    canvas.width = size;
    canvas.height = size;

    // Background
    ctx.fillStyle = '#080608';
    ctx.fillRect(0, 0, size, size);

    // Subtle radial glow
    const grad = ctx.createRadialGradient(size / 2, size * 0.4, 0, size / 2, size * 0.4, size * 0.5);
    grad.addColorStop(0, 'rgba(62, 28, 18, 0.25)');
    grad.addColorStop(1, 'rgba(8, 6, 8, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);

    // Small amber dot
    ctx.beginPath();
    ctx.arc(size / 2, size * 0.38, 4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(196, 137, 74, 0.5)';
    ctx.fill();

    // Title text — Cormorant italic style (using serif fallback)
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255, 243, 225, 0.88)';
    ctx.font = 'italic 300 54px "Cormorant", Georgia, serif';

    // Word wrap the title
    const maxWidth = 720;
    const words = reflection.split(' ');
    const lines = [];
    let currentLine = '';
    for (const word of words) {
      const test = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = test;
      }
    }
    if (currentLine) lines.push(currentLine);

    const lineHeight = 76;
    const titleY = size * 0.45 - ((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, size / 2, titleY + i * lineHeight);
    });

    // Attribution
    ctx.font = '24px "EB Garamond", Georgia, serif';
    ctx.fillStyle = 'rgba(255, 243, 225, 0.25)';
    ctx.fillText('from a conversation with Eunoia', size / 2, size * 0.62);

    return canvas.toDataURL('image/png');
  }, [reflection]);

  const handleShare = async () => {
    setSharing(true);
    try {
      const dataUrl = generateShareCard();
      if (!dataUrl) return;

      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'eunoia-reflection.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: reflection,
          text: reflection
        });
      } else {
        // Fallback: download the image
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = 'eunoia-reflection.png';
        a.click();
      }
    } catch (e) {
      // User cancelled share or error — silent
    } finally {
      setSharing(false);
    }
  };

  const handleKeepPrivate = () => {
    setFadingOut(true);
    setTimeout(() => onDone(), 800);
  };

  return (
    <div
      data-testid="reflection-view"
      style={{
        position: 'fixed',
        inset: 0,
        background: '#080608',
        backgroundImage: 'radial-gradient(ellipse at 50% 40%, rgba(62, 28, 18, 0.3) 0%, rgba(8, 6, 8, 0) 70%)',
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: fadingOut ? 0 : 1,
        transition: 'opacity 0.8s ease-out'
      }}
    >
      {/* Hidden canvas for share card generation */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div style={{ textAlign: 'center', maxWidth: '320px', padding: '0 24px' }}>
        {/* Amber breathing point */}
        <div style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: 'rgba(196, 137, 74, 0.6)',
          margin: '0 auto 32px auto',
          animation: 'reflection-breath 3s ease-in-out infinite'
        }} />

        {/* Poetic title */}
        <p
          data-testid="reflection-title"
          style={{
            fontFamily: "'Cormorant', Georgia, serif",
            fontSize: '28px',
            fontWeight: 300,
            fontStyle: 'italic',
            color: 'rgba(255, 243, 225, 0.88)',
            lineHeight: 1.6,
            maxWidth: '280px',
            margin: '0 auto',
            opacity: showTitle ? 1 : 0,
            transform: showTitle ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 1.5s ease-out, transform 1.5s ease-out'
          }}
        >
          {reflection || ''}
        </p>

        {/* Divider */}
        <div style={{
          width: '40px',
          height: '1px',
          background: 'rgba(255, 243, 225, 0.10)',
          margin: '24px auto',
          opacity: showDivider ? 1 : 0,
          transition: 'opacity 0.8s ease-out'
        }} />

        {/* Options */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '40px',
          opacity: showOptions ? 1 : 0,
          transform: showOptions ? 'translateY(0)' : 'translateY(6px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}>
          <button
            onClick={handleShare}
            disabled={sharing}
            data-testid="share-reflection-btn"
            className="reflection-option"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '13px',
              fontWeight: 400,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'rgba(255, 243, 225, 0.50)',
              padding: '12px 8px',
              WebkitTapHighlightColor: 'transparent',
              transition: 'color 0.2s ease'
            }}
          >
            {sharing ? '...' : 'share this'}
          </button>

          <button
            onClick={handleKeepPrivate}
            data-testid="keep-private-btn"
            className="reflection-option"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '13px',
              fontWeight: 400,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'rgba(255, 243, 225, 0.50)',
              padding: '12px 8px',
              WebkitTapHighlightColor: 'transparent',
              transition: 'color 0.2s ease'
            }}
          >
            keep it private
          </button>
        </div>
      </div>

      <style>{`
        @keyframes reflection-breath {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.3); }
        }
        .reflection-option:active {
          color: rgba(196, 137, 74, 0.80) !important;
        }
      `}</style>
    </div>
  );
};

export default ReflectionView;
